import { useQuery } from '@tanstack/react-query'
import { TrendingUp } from 'lucide-react'
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { registrosApi } from '@/modules/registros/api/registros.api'
import type { GraficaLineasItemConfig } from '@/modules/registros/types'

interface DynamicLineChartProps {
  config: GraficaLineasItemConfig
  configId: number
}

export function DynamicLineChart({ config, configId }: DynamicLineChartProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: [
      'agregacion-lineas',
      configId,
      config.columna_agrupacion,
      config.limite_categorias,
    ],
    queryFn: () =>
      registrosApi.getAgregacionBarras(
        configId,
        config.columna_agrupacion,
        config.limite_categorias
      ),
    enabled: !!config.columna_agrupacion,
    staleTime: 60000,
  })

  if (isLoading) {
    return (
      <Card className="h-full flex flex-col border border-gray-300 dark:border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <Skeleton className="h-4 w-32" />
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-[250px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card className="h-full flex flex-col border border-destructive/50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">
              {config.titulo || 'Tendencia'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground text-sm">
            Error al cargar datos
          </div>
        </CardContent>
      </Card>
    )
  }

  if (data.datos.length === 0) {
    return (
      <Card className="h-full flex flex-col border border-gray-300 dark:border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">
              {config.titulo || 'Tendencia'}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground text-sm">
            Sin datos disponibles
          </div>
        </CardContent>
      </Card>
    )
  }

  const lineColor = config.color || '#14b8a6'

  const chartConfig: ChartConfig = {
    valor: {
      label: 'Total',
      color: lineColor,
    },
  }

  const chartData = data.datos.map((item) => ({
    ...item,
    displayName:
      item.categoria.length > 18
        ? `${item.categoria.substring(0, 18)}...`
        : item.categoria,
  }))

  return (
    <Card className="h-full flex flex-col border border-gray-300 dark:border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">
            {config.titulo || 'Tendencia'}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <ChartContainer config={chartConfig} className="w-full h-[280px]">
          <LineChart
            accessibilityLayer
            data={chartData}
            margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="displayName"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={{ fontSize: 11 }}
              interval={0}
              angle={-35}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={{ fontSize: 11 }}
              width={50}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  formatter={(value, _name, item) => (
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{item.payload.categoria}</span>
                      <span className="text-muted-foreground">
                        Cantidad: {Number(value).toLocaleString('es-EC')}
                      </span>
                    </div>
                  )}
                  hideLabel
                />
              }
            />
            <Line
              dataKey="valor"
              type="monotone"
              stroke={lineColor}
              strokeWidth={2}
              dot={{ fill: lineColor, r: 4 }}
              activeDot={{ r: 6 }}
            >
              {config.mostrar_etiquetas && (
                <LabelList
                  dataKey="valor"
                  position="top"
                  className="fill-foreground"
                  fontSize={11}
                  offset={10}
                  formatter={(value: number) => value.toLocaleString('es-EC')}
                />
              )}
            </Line>
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
